import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { NotificationItem, NotificationListBody, ParentNotificationsService } from './parent-notifications.service';

const PAGE_LIMIT = 10;

// Openers this web portal can actually navigate to — a single mapping
// here, not duplicated between a "push" path and an "in-app" path (unlike
// the mobile app, where NotificationServices.js and NotificationInfo.js
// each hardcode their own copy of the same table and can drift out of
// sync — see the API reference this was built from). Openers with no web
// section yet (Activity: 9/19/20/21, Student Check-In: 18) are left out on
// purpose and simply do nothing when tapped, same as any opener outside
// the mobile app's own NAV_OPENERS list.
const OPENER_SECTION: { [opener: string]: 'broadcast' | 'chat' | 'attendance' | 'diary' | 'leaveRequest' } = {
  '1': 'broadcast',
  '3': 'chat',
  '4': 'chat',
  '6': 'chat',
  '8': 'chat',
  '7': 'attendance',
  '10': 'diary',
  '22': 'leaveRequest',
  '23': 'leaveRequest',
  '24': 'leaveRequest',
};

interface NotificationSection {
  label: string;
  items: NotificationItem[];
}

@Component({
  selector: 'app-parent-notifications',
  templateUrl: './parent-notifications.component.html',
  styleUrls: ['./parent-notifications.component.css'],
})
export class ParentNotificationsComponent implements OnInit, OnChanges {

  @Input() studentId = '';
  @Input() studentSwitchTick = 0;

  @Output() navigate = new EventEmitter<'broadcast' | 'chat' | 'attendance' | 'diary' | 'leaveRequest'>();

  items: NotificationItem[] = [];
  currentPage = 1;
  maxPageNumber = 1;
  loading = false;
  error = '';

  confirmTarget: NotificationItem | 'All' | null = null;

  private requestId = 0;

  constructor(private notificationsService: ParentNotificationsService) { }

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentSwitchTick'] && !changes['studentSwitchTick'].firstChange) {
      this.currentPage = 1;
      this.confirmTarget = null;
      this.loadList();
    }
  }

  loadList(): void {
    this.loading = true;
    this.error = '';
    const requestId = ++this.requestId;
    this.notificationsService.getNotifications(this.studentId, this.currentPage, PAGE_LIMIT).subscribe({
      next: (res: any) => {
        if (requestId !== this.requestId) { return; }
        this.loading = false;
        if (res && res.status && res.body) {
          const body: NotificationListBody = res.body;
          this.items = body.unReadNotificatiion || [];
          this.maxPageNumber = body.maxPageNumber || 1;
        } else {
          this.items = [];
          this.maxPageNumber = 1;
          this.error = (res && res.message) || 'Unable to load notifications.';
        }
      },
      error: (err) => {
        if (requestId !== this.requestId) { return; }
        this.loading = false;
        this.items = [];
        this.error = (err && err.error && err.error.message) || 'Unable to load notifications.';
      },
    });
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.maxPageNumber || page === this.currentPage) { return; }
    this.currentPage = page;
    this.loadList();
  }

  goToPrevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  trackByNotificationId(_: number, item: NotificationItem): number {
    return item.notificationId;
  }

  // Grouped by notifiedOn vs today's date, same split as the mobile
  // screen's Today/Earlier SectionList.
  get sections(): NotificationSection[] {
    const todayKey = this.dateOnly(new Date().toISOString());
    const today: NotificationItem[] = [];
    const earlier: NotificationItem[] = [];
    for (const item of this.items) {
      (this.dateOnly(item.notifiedOn) === todayKey ? today : earlier).push(item);
    }
    const sections: NotificationSection[] = [];
    if (today.length) { sections.push({ label: 'today', items: today }); }
    if (earlier.length) { sections.push({ label: 'earlier', items: earlier }); }
    return sections;
  }

  private dateOnly(iso: string): string {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  // Tapping a notification navigates only — it does NOT mark it read
  // (marking read only happens through the explicit confirm actions below),
  // matching the mobile screen exactly.
  openNotification(item: NotificationItem): void {
    const section = OPENER_SECTION[item.opener];
    if (section) { this.navigate.emit(section); }
  }

  requestMarkRead(item: NotificationItem, event: Event): void {
    event.stopPropagation();
    this.confirmTarget = item;
  }

  requestMarkAllRead(): void {
    if (!this.items.length) { return; }
    this.confirmTarget = 'All';
  }

  cancelConfirm(): void {
    this.confirmTarget = null;
  }

  confirmMarkRead(): void {
    if (!this.confirmTarget) { return; }
    const target = this.confirmTarget;
    this.confirmTarget = null;

    if (target === 'All') {
      this.notificationsService.markNotificationRead(this.studentId, 'All').subscribe();
      this.items = [];
      return;
    }

    this.notificationsService.markNotificationRead(this.studentId, target.notificationId).subscribe();
    this.items = this.items.filter(i => i.notificationId !== target.notificationId);
  }
}
